<template>
    <!-- Optimized Audio Recording Modal -->
    <BaseModal :title="title" @close="closeModal" :visible="true" size="medium">
        <div class="record__audio__popup">
            <div class="assesment__transcribe__wrapper popup__content__box">
                <div class="audio-overlay-message" v-show="['pending', 'processing'].includes(transcriptionStatus)">
                    <img :src="aiImg" class="animate-img" width="200" />
                    <p><b>Hang tight!</b></p>
                    <p>We’re processing your transcription<br /> Thanks for your patience, it’ll be ready shortly.</p>
                </div>
                <div class="popup-vowels-content">
                    <div class="popup-vowels-content-head">
                        <div class="rlms-flex rlms-items-center rlms-gap-5">
                            <div class="rlms-fw-500">Test Item</div>
                            <Tooltip :content="`<div class='info__options__box' style='visibility: visible;opacity:1;'>
                                        <div class='options__group light__purple'>
                                            <div class='info__colors'></div><span class='options__text'>Default</span>
                                        </div>
                                        <div class='options__group light__green'>
                                            <div class='info__colors'></div><span class='options__text'>Correct</span>
                                        </div>
                                        <div class='options__group light__red'>
                                            <div class='info__colors'></div><span class='options__text'>Incorrect</span>
                                        </div>
                                    </div>`" placement="right" width="auto">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                        </div>
                        <div class="rlms-flex rlms-items-center rlms-gap-5">
                            <input type="checkbox" v-model="noResponse" id="response-input" class="rlms-checkbox"
                                :disabled="['pending', 'processing'].includes(transcriptionStatus)"
                                @change="saveTranscribe" />
                            <label for="response-input" class="rlms-fs-xs" style="padding-left: 0px;">No
                                Response</label>
                        </div>
                    </div>
                    <div class="popup-vowels-content-body">
                        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-w-100">
                            <span>{{ item.question }}</span>

                            <div class="rlms-flex rlms-gap-10 rlms-items-center rlms-relative">
                                <Tooltip :content="`${transcriptionMessage}`" placement="left"
                                    v-if="['completed'].includes(transcriptionStatus)" width="200px">
                                    <template #button>
                                        <TranscriptionFeedbackAction v-if="['completed'].includes(transcriptionStatus)"
                                            :attempt="item" :organizationId="organizationId"
                                            :assessmentId="assessmentId" @saved="handleFeedbackEvent" />
                                    </template>
                                </Tooltip>

                                <Tooltip :content="`${transcriptionMessage}`" placement="left"
                                    v-if="transcriptionStatus" width="200px"
                                    :component="['completed'].includes(transcriptionStatus) ? TranscriptionFeedbackAction : null"
                                    :componentProps="{ organizationId: organizationId, assessmentId: assessmentId, attempt: item }"
                                    @tooltipEvent="handleFeedbackEvent">
                                    <template #button>
                                        <img :src="optionsInfoIcon2" width="20" />
                                    </template>
                                </Tooltip>
                            </div>
                        </div>

                        <template v-if="consonantStates?.length && item.consonant">
                            <div class="tbh__labels__group">
                                <template v-for="(c, index) in consonantStates" :key="index">
                                    <template v-if="c.value === '-'"> - </template>
                                    <template v-else-if="c.value === ' '"> &nbsp; </template>
                                    <template v-else>
                                        <span class="label"
                                            :class="[getClassForState(c.state), { 'disabled-label': noResponse }]"
                                            @click="() => ['pending', 'processing'].includes(transcriptionStatus) || noResponse ? null : toggleConsonant(index)"
                                            :title="noResponse ? 'Disabled when No Response is checked' : ''">
                                            {{ c.value }}
                                        </span>
                                    </template>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div class="tbh__labels__group checkbox__group">
                                <template v-for="(c, index) in consonantStates" :key="`${index}-checkbox`">
                                    <div class="single__checkbox" :class="c">
                                        <input type="checkbox"
                                            @click="() => ['pending', 'processing'].includes(transcriptionStatus) || noResponse ? null : toggleConsonant(index)"
                                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || noResponse"
                                            :checked="c.state == 'green'" />
                                        <label></label>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="consonantStates?.length">
                            {{ item.consonant ? PHONEME_COLOUR_LEGEND : SYLLABLE_COUNT_LEGEND }}
                        </p>
                    </div>
                </div>

                <!-- Score Radio Buttons -->
                <div class="popup_point_items"
                    :class="{ disabled_tab: ['pending', 'processing'].includes(transcriptionStatus) === true }">
                    <span class="rlms-fw-500">Score:</span>
                    <div class="point__checkbox" @click="handleScoreClick('1.0')">
                        <input type="radio" :id="`audio_point_${item?.id}_1`" value="1.0" readonly class="markscore"
                            :checked="score === '1.0'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_1`" class="rlms-fs-xs">1 Point</label>
                    </div>
                    <div class="point__checkbox" v-if="group.id <= 3" @click="handleScoreClick('0.5')">
                        <input type="radio" :id="`audio_point_${item?.id}_2`" value="0.5" readonly class="markscore"
                            :checked="score === '0.5'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_2`" class="rlms-fs-xs">1/2 Point</label>
                    </div>
                    <div class="point__checkbox" readonly @click="handleScoreClick('0.0')">
                        <input type="radio" :id="`audio_point_${item?.id}_3`" value="0.0" class="markscore"
                            :checked="score === '0.0'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_3`" class="rlms-fs-xs">0 Point</label>
                    </div>
                </div>
                <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="group?.id">
                    {{ scoringRuleNote(group.id) }}
                </p>

                <!-- Placeholder Image -->
                <div class="transcription-setting-outer">
                    <div class="transcription-setting bg_grey rlms-w-100">
                        <label class="setting-label">
                            Transcription
                            <Tooltip :content="`<p style='font-size: 14px; line-height: 18px;'>
                                        This setting enables or disables automatic transcription of audio recordings.
                                        </p>`" placement="right" backgroundColor="transparent">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                            <span class="badge">Beta</span>
                        </label>
                        <div class="toggle-switch" :style="{
                            opacity: (agreement?.is_accepted && !legal.aiBlocked) ? 1 : 0.5,
                            pointerEvents: (agreement?.is_accepted && !legal.aiBlocked) ? 'auto' : 'none',
                        }">
                            <span class="rlms-fs-xs">Off</span>
                            <label class="switch">
                                <input type="checkbox" id="transcribe-settings" :checked="isTranscriptionEnabled"
                                    @change="updateTranscribeSetting($event)">
                                <span class="slider round"></span>
                            </label>
                            <span class="rlms-fs-xs">On</span>
                        </div>
                    </div>
                    <!-- A new version of the privacy policy or terms pauses the AI
                         features until it is accepted. Everything else on this
                         screen keeps working, including recording by hand. -->
                    <template v-if="legal.aiBlocked">
                        <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">
                            Automatic transcription is paused until you accept the updated privacy policy and
                            terms. You can still record and score by hand.
                        </p>
                    </template>
                    <template v-else-if="!agreement?.is_accepted">
                        <template v-if="assessment?.learner?.is_authorized_user && !assessment?.learner?.is_administrator">
                            <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">Please ask your organization to
                                accept the AI Usage Agreement before using AI features.</p>

                        </template>
                        <template v-else>
                            <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">You must
                                accept the AI Usage Agreement before using AI features. <router-link
                                    :to="`/organizations/${organizationId}/agreements`"
                                    class="rlms-btn rlms-btn-outline-primary rlms-text-primary-400 rlms-fw-500 rlms-mt-3 rlms-mb-2">Click Here to Accept</router-link></p>
                        </template>
                    </template>
                </div>

                <!-- Waveform & Controls -->
                <div class="play__audio__box__wrapper">
                    <div class="recording__title rlms-flex">
                        <h6 class="title rlms-flex rlms-items-center rlms-self-start">Recording &nbsp;
                            <Tooltip :content="`<p style='font-size: 14px; line-height: 18px;'>
                                        <b>🎙️ Recording Instructions</b><br/>
                                        Max Record Limit is ${maxRecordingTime - 1} seconds.<br/>
                                        After recording, most of the clip is selected for you on the audio bar.<br/>
                                        • Only the selected range will be sent for AI transcription.<br/>
                                        • Use <b>Send the whole clip</b> if the response is near the start or the end.<br/>
                                        • The AI will automatically highlight consonants in your transcript for easier review.<br/>
                                        • Drag the handles on the audio bar to fine-tune your selection before confirming.<br/>
                                    </p>`" placement="right" width="380px">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                        </h6>
                        <div class="rlms-flex rlms-gap-10 rlms-items-center">
                            <!-- Control Buttons -->
                            <div class="mic__play__btn_with_timer">
                                <div class="button">
                                    <a href="javascript:void(0);" v-if="!hasRecording && recordingState === 'idle'"
                                        @click="startRecording">
                                        <img :src="recordIcon" />
                                    </a>
                                    <a href="javascript:void(0);" v-if="!hasRecording && recordingState === 'recording'"
                                        @click="stopRecording">
                                        <img :src="stopIcon" />
                                    </a>
                                    <a href="javascript:void(0);"
                                        v-if="hasRecording && recordingState === 'recorded' && !isPlaying"
                                        @click="togglePlayback">
                                        <img :src="playIcon" />
                                    </a>
                                    <a href="javascript:void(0);"
                                        v-if="hasRecording && recordingState === 'recorded' && isPlaying"
                                        @click="togglePlayback">
                                        <img :src="pauseIcon" />
                                    </a>
                                </div>
                                <div class="timer">
                                    {{ formattedTime }}
                                </div>
                            </div>
                            <div class="edit_audio_button" v-if="hasRecording && recordingState !== 'recording'">
                                <a href="javascript:void(0);" @click="resetRecording">
                                    <img :src="redoBtn" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2">
                        {{ RECORDING_SETUP_NOTE }}
                    </p>

                    <!-- This item had a recording and its audio file has gone.
                         Say so, rather than showing an empty recorder as
                         though nothing was ever recorded. -->
                    <p class="note-text rlms-fs-xxs rlms-text-danger-300 rlms-mt-2" v-if="unavailableDetail">
                        <b>{{ RECORDING_UNAVAILABLE_TITLE }}.</b> {{ unavailableDetail }} You can record this item
                        again.
                    </p>

                    <div ref="waveformContainer" class="waveform-container" style="width: 100%;"
                        v-show="hasRecording || recordingState === 'recording'"></div>

                    <!-- Which part of the clip gets transcribed -->
                    <div class="audio-selection-row rlms-flex rlms-items-center rlms-gap-10 rlms-mt-2"
                        v-if="canChangeSelection">
                        <a href="javascript:void(0);"
                            class="rlms-btn rlms-btn-outline-primary rlms-text-primary-400 rlms-fw-500"
                            @click="selectWholeClip" v-if="!isWholeClipSelected">Send the whole clip</a>
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mb-0">
                            {{ selectionLabel }} Drag the handles on the audio bar to change it.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Footer -->
        <template #footer>
            <div class="audio-nav">
                <a href="javascript:void(0)" class="prev" @click="goTo('prev')" :style="{
                    opacity: recordingState !== 'recording' ? 1 : 0.5,
                    pointerEvents: recordingState !== 'recording' ? 'auto' : 'none',
                    cursor: recordingState !== 'recording' ? 'pointer' : 'not-allowed'
                }">
                    <span class="btn__icon">
                        <img src="@/assets/images/arrow-left.png">
                    </span>
                    <span class="btn__text">Prev</span>
                </a>
                <div class="purple__solid__btn" v-if="audioBlob">
                    <a href="javascript:void(0)" id="saveAndClose" @click="saveAudio()" :style="{
                        opacity: audioBlob ? 1 : 0.5,
                        pointerEvents: audioBlob ? 'auto' : 'none',
                        cursor: audioBlob ? 'pointer' : 'not-allowed'
                    }">
                        Save
                    </a>
                </div>
                <a href="javascript:void(0)" class="next" @click="goTo('next')" :style="{
                    opacity: recordingState !== 'recording' ? 1 : 0.5,
                    pointerEvents: recordingState !== 'recording' ? 'auto' : 'none',
                    cursor: recordingState !== 'recording' ? 'pointer' : 'not-allowed'
                }">
                    <span class="btn__text">Next</span>
                    <span class="btn__icon">
                        <img src="@/assets/images/arrow-right1.png">
                    </span>
                </a>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import BaseModal from '../BaseModal.vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import { showConfirmDialog, showDialog, showToast } from '@/utils/swal';
import { useLoaderStore } from '@/stores/loader';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import redoBtn from '@/assets/images/rotate-left.png';
import recordIcon from '@/assets/images/audio/record.png';
import stopIcon from '@/assets/images/audio/stop.png';
import playIcon from '@/assets/images/audio/play.png';
import pauseIcon from '@/assets/images/audio/pause.png';
import { useAuthStore } from '@/stores/auth';
import optionsInfoIcon from '@/assets/images/info-circle 2.png';
import optionsInfoIcon2 from '@/assets/images/info-circle.png';
import { authrizedPermissions } from '@/utils/permissions';
import Tooltip from '../Tooltip.vue';
import aiImg from '@/assets/images/ai-transcription-img.png';
import TranscriptionFeedbackAction from './TranscriptionFeedbackAction.vue';
import { convertToWav } from '@/utils/audioToWav';
import { PHONEME_COLOUR_LEGEND, RECORDING_SETUP_NOTE, SYLLABLE_COUNT_LEGEND, scoringRuleNote } from '@/utils/assessment-notes';
import { RECORDING_UNAVAILABLE_TITLE, recordingUnavailableDetail } from '@/utils/recording-retention';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import { useLegalStore } from '@/stores/legal';

const isTranscriptionEnabled = ref(false);

const authStore = useAuthStore();
const emit = defineEmits(['close', 'saved', 'goto', 'scored', 'feedback']);

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    maxRecordingTime: { type: Number, default: 31 },
    item: Object,
    canConductAssessment: Boolean,
    assessment: Object,
    group: Object,
    agreement: { type: Object, default: null },
});

const title = 'Record Audio';
const waveformContainer = ref(null);
const wavesurfer = ref(null);
const regions = RegionsPlugin.create();
const wavesurferRecord = ref(null);
const audioUrl = ref('');
const audioBlob = ref(null);
const audioDuration = ref(null);
const recordingState = ref('idle');
const hasRecording = ref(false);
const isPlaying = ref(false);
const timeElapsed = ref(0);
let timerInterval = null;
const audioStartTime = ref(null);
const audioEndTime = ref(null);
const noResponse = ref(false);
const transcriptionStatus = ref(null);

const SELECTION_COLOR = 'rgba(114, 92, 255, 0.50)';

// Set only while this item's audio file is gone: it clears the moment
// something is recorded again, because that reads the item's own `audio`.
const unavailableDetail = computed(() => recordingUnavailableDetail(props.item));

// The part of the clip sent for transcription. A narrow default window cut off
// the start or the end of short responses, so the default is forgiving: most of
// the clip, and never less than this many seconds.
const DEFAULT_SELECTION_MIN_SECONDS = 4;
const DEFAULT_SELECTION_RATIO = 0.8;

const defaultSelection = (duration) => {
    const clip = Number(duration) || 0;
    const width = Math.max(DEFAULT_SELECTION_MIN_SECONDS, clip * DEFAULT_SELECTION_RATIO);

    if (width >= clip) return { start: 0, end: clip };

    const mid = clip / 2;
    return {
        start: Math.max(0, mid - width / 2),
        end: Math.min(clip, mid + width / 2),
    };
};

// A selection already saved with the recording, which is not editable.
const hasSavedSelection = () =>
    audioStartTime.value !== null && audioStartTime.value !== undefined && audioStartTime.value !== ''
    && audioEndTime.value !== null && audioEndTime.value !== undefined && audioEndTime.value !== '';

// The selection can only be changed before the recording is saved.
const canChangeSelection = computed(() =>
    !!audioBlob.value && recordingState.value === 'recorded'
    && !['pending', 'processing'].includes(transcriptionStatus.value)
);

const isWholeClipSelected = computed(() => {
    const clip = Number(audioDuration.value) || 0;
    if (!clip) return false;

    return Number(audioStartTime.value) <= 0.05 && Number(audioEndTime.value) >= clip - 0.05;
});

const selectionLabel = computed(() => {
    if (audioStartTime.value === null || audioEndTime.value === null) return '';

    const start = Number(audioStartTime.value);
    const end = Number(audioEndTime.value);
    if (!isFinite(start) || !isFinite(end)) return '';

    return `Sending ${(end - start).toFixed(1)}s of this recording (${start.toFixed(1)}s to ${end.toFixed(1)}s).`;
});

// Send the whole clip: one click instead of dragging the handles to the ends.
const selectWholeClip = () => {
    if (!wavesurfer.value) return;

    const duration = wavesurfer.value.getDuration();
    if (!duration) return;

    regions.clearRegions();
    regions.addRegion({
        start: 0,
        end: duration,
        color: SELECTION_COLOR,
        drag: true,
        resize: true,
    });
};

// Reactive references to item properties
const score = computed({
    get: () => props.item?.score,
    set: (val) => {
        emit('scored', { type: 'score', id: props.item?.id, value: val });
    }
});

const updateScore = (value) => {
    score.value = (score.value === value) ? null : value;
};

function handleScoreClick(value) {
    if (['pending', 'processing'].includes(transcriptionStatus.value)) {
        return;
    }

    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props?.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to score this assessment because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to score this assessment because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    updateScore(value);
}

const consonantScore = ref([]);
const consonantStates = ref([]);

const loader = useLoaderStore();
const legal = useLegalStore();

const formattedTime = computed(() => {
    const min = Math.floor(timeElapsed.value / 60);
    const sec = timeElapsed.value % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
});

onMounted(async () => {
    await fetchUser();
});

onBeforeUnmount(() => {
    clearTimer();
    if (wavesurfer.value) wavesurfer.value.pause();
    if (audioUrl.value && !props.audio) URL.revokeObjectURL(audioUrl.value);
});

const closeModal = () => emit('close');

const initWaveSurfer = (mode = 'playback') => {
    if (wavesurfer.value) wavesurfer.value.pause();
    if (wavesurfer.value) wavesurfer.value.destroy();

    wavesurfer.value = WaveSurfer.create({
        normalize: true,
        barWidth: 3,
        barGap: 1,
        barRadius: 1,
        height: 80,
        cursorWidth: 2,
        container: waveformContainer.value,
        backend: 'WebAudio',
        progressColor: 'rgba(114,92,255,1)',
        waveColor: 'rgba(210,199,254,1)',
        cursorColor: "#4a74a5",
        plugins: [regions]
    });

    if (mode === 'recording') {
        wavesurferRecord.value = wavesurfer.value.registerPlugin(RecordPlugin.create({
            renderRecordedAudio: true,
            scrollingWaveform: true,
            continuousWaveform: false,
        }));

        wavesurferRecord.value.on('record-end', async (blob) => {
            const wavBlob = await convertToWav(blob);
            audioBlob.value = wavBlob;
            audioUrl.value = URL.createObjectURL(wavBlob);

            hasRecording.value = true;
            recordingState.value = 'recorded';

            initWaveSurfer('playback');

            // audioBlob.value = blob;
            // audioUrl.value = URL.createObjectURL(blob);
            // hasRecording.value = true;
            // recordingState.value = 'recorded';
            // initWaveSurfer('playback');
        });

        wavesurferRecord.value.on('record-progress', (ms) => {
            timeElapsed.value = Math.floor(ms / 1000);
            if (timeElapsed.value >= props.maxRecordingTime) stopRecording();
        });

        wavesurferRecord.value.startRecording().catch((err) => {
            showDialog({ title: err?.message ?? 'Microphone access was denied or not available.' });
            resetRecording();
        });
    } else {
        loader.show();
        wavesurfer.value.load(audioUrl.value);

        wavesurfer.value.on('ready', () => {
            audioDuration.value = Math.floor(wavesurfer.value.getDuration());
            timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
            loader.hide();
        });
        wavesurfer.value.on('play', () => { if (!isPlaying.value) timeElapsed.value = 0; });

        wavesurfer.value.on('audioprocess', (currentTime) => {
            if (currentTime <= Math.floor(wavesurfer.value.getDuration())) {
                timeElapsed.value = Math.floor(currentTime);
            } else {
                setTimeout(() => {
                    wavesurfer.value.stop();
                    isPlaying.value = false;
                    timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
                }, 500);
            }
        });

        wavesurfer.value.on('finish', () => {
            isPlaying.value = false;
            timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
        });

        wavesurfer.value.on('decode', () => {
            if (hasSavedSelection()) {
                regions.addRegion({
                    start: Number(audioStartTime.value),
                    end: Number(audioEndTime.value),
                    color: SELECTION_COLOR,
                    drag: false,
                    resize: false,
                });
            } else {
                const { start, end } = defaultSelection(wavesurfer.value.getDuration());

                regions.addRegion({
                    start,
                    end,
                    color: SELECTION_COLOR,
                    drag: true,
                    resize: true,
                });
            }
        })

        let loop = false

        {
            let activeRegion = null
            regions.on('region-in', () => {
                // activeRegion = region
                isPlaying.value = true
            })
            regions.on('region-out', (region) => {
                if (activeRegion === region) {
                    if (loop) {
                        region.play()
                    } else {
                        activeRegion = null
                        isPlaying.value = false;
                    }
                }
            })
            regions.on('region-clicked', (region, e) => {
                e.stopPropagation() // prevent triggering a click on the waveform
                activeRegion = region
                region.play(true);
                region.setOptions({ color: 'rgba(114, 92, 255, 0.50)' })
            })

            // Reset the active region when the user clicks anywhere in the waveform
            wavesurfer.value.on('interaction', () => {
                activeRegion = null
            });

            regions.on('region-created', (region) => {
                const allRegions = regions.getRegions();

                if (allRegions.length > 1) {
                    // Remove the old regions, keep only the latest one
                    allRegions
                        .filter(r => r.id !== region.id)
                        .forEach(oldRegion => oldRegion.remove());
                }

                console.log('Selected range:', region.start, region.end);

                audioStartTime.value = region.start;
                audioEndTime.value = region.end;
            });

            regions.on('region-updated', (region) => {
                const allRegions = regions.getRegions();

                if (allRegions.length > 1) {
                    // Remove the old regions, keep only the latest one
                    allRegions
                        .filter(r => r.id !== region.id)
                        .forEach(oldRegion => oldRegion.remove());
                }

                console.log('Selected range updated:', region.start, region.end);

                audioStartTime.value = region.start;
                audioEndTime.value = region.end;
            });
        }

    }
};

const startRecording = () => {
    timeElapsed.value = 0;
    recordingState.value = 'recording';
    initWaveSurfer('recording');
};

const stopRecording = () => {
    if (recordingState.value === 'recording' && wavesurferRecord.value?.isRecording()) {
        wavesurferRecord.value.stopRecording();
        clearTimer();
        recordingState.value = 'recorded';
    }
};

const togglePlayback = () => {
    if (!wavesurfer.value) return;
    isPlaying.value ? wavesurfer.value.pause() : wavesurfer.value.play();
    isPlaying.value = !isPlaying.value;
};

const resetRecording = () => {
    wavesurfer.value?.pause();
    wavesurfer.value?.destroy();
    if (audioUrl.value && !props.audio) URL.revokeObjectURL(audioUrl.value);

    audioUrl.value = '';
    audioBlob.value = null;
    hasRecording.value = false;
    isPlaying.value = false;
    recordingState.value = 'idle';
    timeElapsed.value = 0;
    audioStartTime.value = null;
    audioEndTime.value = null;
};

const clearTimer = () => {
    if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
    }
};

const saveAudio = async () => {
    try {
        if (audioStartTime.value === null || audioEndTime.value === null) {
            showDialog({
                icon: 'warning',
                html: 'Please select the audio range for transcription'
            });

            return;
        }

        loader.show();
        const formData = new FormData();
        formData.append('attemptid', props.item?.id);
        if (audioBlob.value) {
            // const cleanBlob = new Blob([audioBlob.value], { type: 'audio/webm' });
            const cleanBlob = new Blob([audioBlob.value], { type: 'audio/wav' });
            formData.append('audio', cleanBlob, 'audio.wav');
        }

        formData.append('duration', audioDuration.value);
        formData.append('start_time', audioStartTime.value);
        formData.append('end_time', audioEndTime.value);

        const { data } = await api.post(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        emit('saved', {
            type: 'audio',
            id: props.item?.id,
            value: data?.data?.audio,
            extra: {
                duration: data?.data?.duration,
                start_time: data?.data?.start_time,
                end_time: data?.data?.end_time,
                transcription_status: data?.data?.transcription_status
            }
        });

        audioBlob.value = null;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const goTo = async (direction = 'next') => {
    if (audioBlob.value) {
        const capitalizedDirection = direction.charAt(0).toUpperCase() + direction.slice(1);

        const result = await showConfirmDialog({
            title: 'Are you sure?',
            html: '<p id="text-message">Are you sure?</p><span class="rlms-text-danger-500" id="delete-confirm"> You have an unsaved audio recording.</span>',
            showCancelButton: true,
            confirmButtonColor: '#725cff',
            cancelButtonColor: '#725cff',
            confirmButtonText: `Save & ${capitalizedDirection}`,
            cancelButtonText: 'Skip',
            confirmButtonClass: 'rlms-btn rlms-btn-primary',
            cancelButtonClass: 'rlms-btn rlms-btn-default',
        });

        if (result.isConfirmed) {
            await saveAudio();
        }
    }

    // closeModal();
    if (direction === 'next') {
        emit('goto', { id: props.item?.id, direction });
    } else if (direction === 'prev') {
        emit('goto', { id: props.item?.id, direction });
    }

    resetRecording();
};

const fetchAudioBlobUrl = async (url) => {
    if (!url) return null;

    try {
        loader.show();
        const token = useAuthStore().token;
        const response = await api.get(url, {
            responseType: "blob",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return URL.createObjectURL(response.data);
    } catch (error) {
        handleError(error);

        return null;
    } finally {
        loader.hide();
    }
};

const updateTranscribeSetting = async (event) => {
    try {
        // A new version of the privacy policy or terms pauses the AI features
        // until it is accepted; everything else on this screen keeps working.
        if (!aiFeaturesAllowed()) {
            event.target.checked = false;

            return;
        }

        if (!props?.agreement?.is_accepted) {
            event.target.checked = false;
            showToast({
                icon: 'warning',
                title: 'Warning! ',
                text: 'You must accept the AI Usage Agreement before using AI features.',
            });

            return;
        }

        loader.show();

        await api.patch('/user/update', {
            automated_transcribe_enabled: event.target.checked
        });

        showToast({
            icon: 'success',
            title: 'Success! ',
            text: 'Transcribe setting updated successfully.',
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const fetchUser = async () => {
    try {
        loader.show();

        await authStore.fetchUser();
        const user = authStore?.userinfo;

        if(props.agreement?.is_accepted) {
            isTranscriptionEnabled.value = user.automated_transcribe_enabled;
        }
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

// Watch item and initialize state
watch(() => props.item, async (newItem, oldItem) => {
    noResponse.value = newItem?.no_response === 'Y';
    transcriptionStatus.value = newItem?.transcription_status;

    // Parse consonants
    let parsedConsonants = [];
    try {
        if (newItem?.consonant) {
            parsedConsonants = JSON.parse(newItem?.consonant?.replace(/'/g, '"')) || [];
        } else {
            parsedConsonants = newItem?.question.split('-');
        }
    } catch {
        parsedConsonants = [];
    }

    let parsedConsonantScore = [];
    parsedConsonantScore = typeof newItem?.consonant_score === 'string'
        ? JSON.parse(newItem.consonant_score)
        : newItem?.consonant_score || [];

    // Map to state
    consonantStates.value = parsedConsonants.map((value, index) => {
        // Every slot starts unscored, including groups 4 and 5 (which have no
        // consonant list): a slot is only red once a 0 has actually been recorded.
        let state = 'default';
        const score = parsedConsonantScore?.[index];

        if (value === '-' || value === ' ') state = 'green';
        else if (score == 1 || score === true) state = 'green';
        else if (score == 0 || score === false) state = 'red';
        return { id: index, value, state };
    });

    if (newItem?.audio !== oldItem?.audio) {
        audioUrl.value = await fetchAudioBlobUrl(newItem.audio);
    }

    audioStartTime.value = newItem?.start_time;
    audioEndTime.value = newItem?.end_time;

    if (audioUrl.value) {
        hasRecording.value = true;
        recordingState.value = 'recorded';
        initWaveSurfer('playback');
    } else {
        resetRecording();
    }
}, { immediate: true, deep: true });

// Assign class based on consonant state
const getClassForState = (state) => {
    if (state === 'green') return 'green';
    if (state === 'red') return 'red';
    return 'purple';
};

// Toggle consonant state
const toggleConsonant = async (index) => {
    const current = consonantStates.value[index].state;
    consonantStates.value[index].state =
        current === 'default' || current === 'red' ? 'green' : 'red';

    await saveTranscribe();
};

// Handle saving transcription data
const saveTranscribe = async () => {
    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to score this assessment because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to score this assessment because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    // if (anyDefault.value && !noResponse.value) {
    //     showDialog({ icon: 'warning', html: 'Please mark the phonetics' });
    //     return;
    // }

    try {
        loader.show();

        consonantStates.value.forEach((item, index) => {
            consonantScore.value[index] = item.state === 'green' ? 1 : item.state === 'red' ? 0 : null;
        });

        const { data } = await api.post(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt`,
            {
                attemptid: props.item.id,
                consonant_score: consonantScore.value,
                no_response: noResponse.value ? 'Y' : 'N',
            }
        );

        showToast({
            icon: 'success',
            title: 'Successful',
            text: 'Transcribe details saved successfully',
        });

        transcriptionStatus.value = data?.data?.transcription_status;

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const transcriptionMessage = computed(() => {
    if (transcriptionStatus.value != 'manual') {
        switch (transcriptionStatus.value) {
            case "processing":
            case "pending":
                return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
                  Your transcription is currently in progress. Please wait and check back later.
                </p>`;
            case "completed":
                return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
                  The transcription from the audio was automatically generated and may be incorrect. You can report any errors by clicking the dislike icon and verify it manually <br/> Alternatively, click the like icon if you’re satisfied with the AI response. Your feedback is valuable to us.
                </p>
                `;
            case "failed":
                return `<p class="rlms-text-danger-400" style="font-size:14px;line-height:18px;">
                  Automated Transcription failed. Please try again or contact support.
                </p>`;
            default:
                return "";
        }
    } else {
        return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
              You have manually overridden the transcription.
            </p>`;
    }
});

const handleFeedbackEvent = (data) => {
    emit('feedback', { attemptId: props.item.id, feedback: data });
};
</script>
